import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Modal, Tag } from 'antd'
import { useLocation } from 'react-router-dom'
import { setItemLocalStorage, removeItemLocalStorage } from 'utils/local-storage'
import {
  ESTABLISHMENT_APP_VERSION_KEY,
  ESTABLISHMENT_APP_VERSION_DISMISSED_KEY
} from 'constants/establishment'
import {
  getRemoteAppVersion,
  getLoadedAppVersion,
  dismissAppVersion,
  isAppVersionDismissed,
  compareAppVersions
} from 'utils/app-version'

/**
 * Avisa que hay una version nueva y deja que el operador decida cuando aplicarla.
 *
 * Antes el interceptor de respuestas recargaba la pagina en el acto apenas la
 * version del backend era mayor. Eso recargaba en cualquier momento, incluso
 * con un formulario a medio llenar, y no resolvía el problema de fondo:
 * `location.reload()` no salta la cache del navegador, asi que el HTML viejo
 * Volvia a servirse y hacia falta un Ctrl+F5.
 *
 * Aqui la recarga la decide el operador, y cuando la decide se revalida el HTML
 * contra el servidor para que de verdad traiga el bundle nuevo.
 *
 * No lleva estado en redux: es una comprobacion de UI, no datos de la aplicacion.
 */
const AppVersionCheck = () => {
  const { pathname } = useLocation()
  const [remoteVersion, setRemoteVersion] = useState(null)
  const [updating, setUpdating] = useState(false)
  // Evita chequeos solapados cuando se navega mas rapido de lo que responde la API.
  const checking = useRef(false)

  const checkVersion = useCallback(async () => {
    if (checking.current) return
    checking.current = true

    try {
      const remote = await getRemoteAppVersion()
      if (!remote) return

      const loaded = getLoadedAppVersion()

      // Primer arranque en este navegador: se adopta la version que ya esta
      // corriendo y no se avisa. Nadie hizo nada todavia, no hay nada que avisar.
      if (!loaded) {
        setItemLocalStorage(ESTABLISHMENT_APP_VERSION_KEY, remote)
        return
      }

      // Parte por parte: con `>` sobre el texto, "2.5.9" pareceria mayor que "2.5.13".
      if (compareAppVersions(remote, loaded) <= 0) return

      // "Ahora no" silencia el aviso, no para siempre: el silencio caduca y una
      // version mas nueva lo ignora por completo.
      if (isAppVersionDismissed(remote)) return

      setRemoteVersion(remote)
    } finally {
      checking.current = false
    }
  }, [])

  // Al cargar y en cada cambio de ruta.
  useEffect(() => {
    checkVersion()
  }, [pathname, checkVersion])

  /*
   * Reabrir el navegador puede restaurar la pagina desde la cache de navegacion
   * sin volver a pedir el HTML, dejando el bundle viejo vivo y sin que se note.
   * `persisted` avisa exactamente de ese caso.
   */
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) checkVersion()
    }

    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [checkVersion])

  const handleDismiss = () => {
    dismissAppVersion(remoteVersion)
    setRemoteVersion(null)
  }

  const handleUpdate = async () => {
    setUpdating(true)

    /*
     * La version guardada se mueve recien aqui, no al detectar. Si se guardara al
     * detectar, "Ahora no" jamas volveria a avisar porque la comparacion daria
     * "ya esta al dia".
     */
    setItemLocalStorage(ESTABLISHMENT_APP_VERSION_KEY, remoteVersion)
    removeItemLocalStorage(ESTABLISHMENT_APP_VERSION_DISMISSED_KEY)
    setRemoteVersion(null)

    /*
     * `reload()` a secas no salta la cache: el navegador puede volver a servir
     * el index.html viejo y el usuario acaba viendo la version anterior otra
     * vez. Pedir el documento con `cache: 'reload'` lo obliga a revalidarlo
     * contra el servidor, y el reload siguiente ya entra con el HTML nuevo.
     */
    try {
      await fetch(`${window.location.pathname}${window.location.search}`, { cache: 'reload' })
    } catch (error) {
      console.error(error)
    }

    window.location.reload()
  }

  return (
    <Modal
      centered
      visible={!!remoteVersion}
      onCancel={handleDismiss}
      onOk={handleUpdate}
      okText="Actualizar"
      cancelText="Más tarde"
      okType="success"
      confirmLoading={updating}
      // Cerrar con Escape o con la X, pero no con un clic al lado: una
      // decision al azar no debería contar como "Más tarde".
      maskClosable={false}
      width={420}
      wrapClassName="app-version-modal"
      title={
        // flex-start y no center: la columna de texto es mas alta que el avatar
        // al sumarle la descripcion, y centrarla dejaba al circulo flotando.
        <div className="d-flex align-items-start">
          {/* Avatar del aviso: da el toque de icono en lugar de texto plano. */}
          <span
            className="d-inline-flex align-items-center justify-content-center mr-3"
            style={{
              width: 54,
              height: 54,
              minWidth: 54,
              borderRadius: '50%',
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              fontSize: 26,
              color: '#52c41a',
              marginTop: 2
            }}
          >
            <i className="fas fa-cloud-download-alt" />
          </span>

          <div>
            <div style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.3 }}>
              Nueva versión disponible
            </div>

            <Tag color="success" className="mt-1 mb-0" style={{ fontSize: 13 }}>
              {remoteVersion}
            </Tag>

            <p className="app-version-modal__note">
              La actualización recargará la página.
            </p>
          </div>
        </div>
      }
    />
  )
}

export default AppVersionCheck